import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import CustomLegend from "./CustomLegend";

interface ChartData {
  xLabel: string;
  xLabel1: string;
  [key: string]: string | number;
}

interface LegendItem {
  key: string;
  label: string;
  color: string;
}

interface BarChartContainerProps {
  height?: number;
  data: ChartData[];
  legendConfig: LegendItem[];
}

/**
 * HorizontalBarChartContainer component renders a responsive bar chart using Recharts
 * to visualize user data over different quarters and years.
 *
 * @component
 * @param {ChartData[]} data - The data array containing user counts per quarter and year.
 *
 * @example
 * <HorizontalBarChartContainer data={sampleData} />
 */
const HorizontalBarChartContainer: React.FC<BarChartContainerProps> = ({
  height = 350,
  data,
  legendConfig,
}) => {
  const reshapedData = legendConfig.map((legend) => ({
    key: legend.key,
    riskType: legend.label,
    value: data[0]?.[legend.key] ?? 0,
  }));

  const labelToColorMap = Object.fromEntries(
    legendConfig.map((item) => [item.key, item.color])
  );

  const legendItems = legendConfig.map((item) => ({
    name: item.label,
    color: item.color,
  }));

  return (
    <>
      <CustomLegend items={legendItems} itemsPerRow={2} />
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          layout="vertical"
          data={reshapedData}
          margin={{ top: 20, right: 30, left: 5, bottom: 20 }}
          barSize={30}
        >
          <CartesianGrid horizontal={false} vertical strokeDasharray="3" />
          <XAxis
            type="number"
            tick={{ fontSize: 12, fill: "#626262" }}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="riskType"
            tick={{ fontSize: 12, fill: "#626262" }}
            tickLine={false}
          />
          <Tooltip />
          <Bar dataKey="value" label={{ position: "right" }}>
            {reshapedData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                data-testid={`bar-${entry.key}`}
                fill={labelToColorMap[entry.key] || "#ccc"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default HorizontalBarChartContainer;
